import { useA11y } from "@/context/A11yContext";
import { ListenButton } from "@/components/ListenButton";
import { WholeLessonListen } from "@/components/WholeLessonListen";
import { LessonHelper } from "@/components/LessonHelper";
import { FindTheNote } from "@/components/FindTheNote";
import { NoteGames } from "@/components/games/NoteGames";
import { ChordKeys } from "@/components/ChordKeys";
import { MarkGames } from "@/components/games/MarkGames";
import { DrumBeatPlayer } from "@/components/DrumBeatPlayer";
import { Logo } from "@/components/Logo";
import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Play, Lightbulb, CheckCircle2, Volume2, Lock, Clock, Church, ListChecks, GraduationCap, SunMoon } from "lucide-react";
import { lessonImage } from "@/data/lessonImages";
import { Navbar } from "@/components/Navbar";
import { StaffViewer } from "@/components/StaffViewer";
import { QuizEngine } from "@/components/QuizEngine";
import { Button } from "@/components/ui/button";
import { getLesson, getLessonContext, nextLessonInCourse, lessonUnlocked } from "@/data/curriculum";
import { playSequence, stopSequence } from "@/lib/audio";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Breadcrumbs } from "@/components/CourseNav";
import { LessonRecap } from "@/components/LessonRecap";
import { PlayAlong } from "@/components/PlayAlong";
import { lessonTrack } from "@/data/playalong";
import { PianoKeyboard } from "@/components/PianoKeyboard";
import { NoteValuesChart, SheetMusicExample } from "@/components/NoteValuesChart";
import { MissingNotes } from "@/components/MissingNotes";
import { SectionText } from "@/components/SectionText";
import { LessonIntro, SummaryVideo } from "@/components/LessonIntro";
import { api } from "@/lib/api";
import { ManuscriptLinks } from "@/components/ManuscriptLinks";
import { RhythmClap } from "@/components/RhythmClap";
import { RangeKeyboard } from "@/components/RangeKeyboard";
import { VoiceRangeFinder } from "@/components/VoiceRangeFinder";
import { ScaleRangeFinder } from "@/components/ScaleRangeFinder";
import { ClefDrawing } from "@/components/ClefDrawing";
import { TermsGrid } from "@/components/TermsGrid";
import { NotationDiagram } from "@/components/NotationDiagrams";
import { BarBuilder } from "@/components/BarBuilder";
import { RestSpotter } from "@/components/RestSpotter";
import { RestFlashCards } from "@/components/RestFlashCards";
import { QuaverStemQuiz } from "@/components/QuaverStemQuiz";
import { BeamedQuaverQuiz, BeamDirectionDiagram } from "@/components/BeamedQuaverQuiz";
import { BeamBuilder } from "@/components/BeamBuilder";
import { ToneSemitoneQuiz } from "@/components/ToneSemitoneQuiz";
import { ToneTapper } from "@/components/ToneTapper";
import { AccidentalSpotter } from "@/components/AccidentalSpotter";
import { GameBoundary } from "@/components/GameBoundary";
import { FixRhythm } from "@/components/FixRhythm";
import { TimeSigMatch } from "@/components/TimeSigMatch";

import { GrooveBuilder } from "@/components/GrooveBuilder";
import { VoicePartSongs } from "@/components/VoicePartSongs";
import { ChoirPractice } from "@/components/ChoirPractice";
import { CourseCertificateCta } from "@/components/CourseCertificateCta";
import { NoteValueLadder } from "@/components/NoteValueLadder";
import { Check, X } from "lucide-react";
import { LessonScores } from "@/components/LessonScores";
import { GuitarDiagram } from "@/components/GuitarDiagram";
import { DrumKitDiagram } from "@/components/DrumKitDiagram";
import { StrumPatterns } from "@/components/StrumPatterns";
import { Fretboard, ChordBox, strum } from "@/components/games/GuitarParts";

const clefLabel = (c) => (c === "none" ? "no clef" : c === "alto" ? "C clef (alto)" : c === "tenor" ? "C clef (tenor)" : `${c} clef`);

const StaveGrid = ({ staves, i }) => (
  <div className={`mt-5 grid gap-3 ${staves.length > 1 ? "sm:grid-cols-2" : ""}`} data-testid={`lesson-staves-${i}`}>
    {staves.map((sv, k) => (
      <div key={k} className={`relative rounded-2xl border bg-card p-3 sm:p-4 ${sv.verdict === "wrong" ? "border-red-500/60" : sv.verdict === "right" ? "border-emerald-500/60" : "border-border"}`} data-testid={`lesson-stave-${i}-${k}`}>
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] uppercase tracking-widest text-primary">{clefLabel(sv.clef)}</span>
          {sv.verdict && (
            <span className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${sv.verdict === "wrong" ? "bg-red-500" : "bg-emerald-500"}`} data-testid={`stave-verdict-${sv.verdict}-${i}-${k}`}>
              {sv.verdict === "wrong" ? <X size={18} strokeWidth={3} /> : <Check size={18} strokeWidth={3} />}
            </span>
          )}
        </div>
        <StaffViewer clef={sv.clef} notes={sv.notes} width={staves.length > 1 ? 300 : 560} showLabels showLetters lineNumbers={sv.lineNumbers}
          playOnClick={sv.clef !== "none"} testId={`lesson-stave-svg-${i}-${k}`} />
        {sv.caption && <p className="mt-1 text-center text-xs font-bold text-foreground/80">{sv.caption}</p>}
      </div>
    ))}
  </div>
);

const LessonPrevNext = ({ lesson, ctx, prefix = "lesson", className = "mb-4" }) => {
  if (!ctx) return null;
  const list = ctx.course.lessons;
  const i = list.findIndex((l) => l.id === lesson.id);
  const prev = list[i - 1];
  const next = list[i + 1];
  const cls = "inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary";
  return (
    <div className={`${className} flex items-center justify-between gap-2`} data-testid={`${prefix}-prev-next`}>
      {prev ? <Link to={`/lessons/${prev.id}`} className={cls} data-testid={`${prefix}-prev-btn`}><ArrowLeft size={13} /> Previous lesson</Link> : <span />}
      {next && <Link to={`/lessons/${next.id}`} className={cls} data-testid={`${prefix}-next-btn`}>Next lesson <ArrowRight size={13} /></Link>}
    </div>
  );
};

const CourseProgress = ({ lessons, number, done }) => {
  const count = lessons.filter((l) => done.has(l.id)).length;
  return (
    <div className="mt-4" data-testid="lesson-course-progress">
      <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        <span data-testid="lesson-course-progress-label">Lesson {number} of {lessons.length}</span>
        <span data-testid="lesson-course-progress-done">{count}/{lessons.length} completed</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-background/60">
        <div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${(count / lessons.length) * 100}%` }} data-testid="lesson-course-progress-bar" />
      </div>
    </div>
  );
};

const seqToNote = (s, k) => {
  const playable = s.notes.map((n, i) => (n.pitch || n.pitches ? i : -1)).filter((i) => i >= 0);
  if (playable.length === s.sequence.length) return playable[k];
  return s.notes.findIndex((n) => (n.pitches || [n.pitch]).includes(s.sequence[k]));
};

export default function LessonDetail() {
  const { id } = useParams();
  return <LessonPage key={id} />;
}

function LessonPage() {
  const { id } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => stopSequence, [id]);
  const nav = useNavigate();
  const lesson = getLesson(id);
  const { user, progress, purchases, completeLesson, touchLesson, saveQuiz } = useAuth();
  const { rate } = useA11y();
  const [showQuiz, setShowQuiz] = useState(false);
  const [gameOn, setGameOn] = useState(false);
  const [active, setActive] = useState(null);
  const [readingIdx, setReadingIdx] = useState(null);
  const followSection = useCallback((n) => {
    setReadingIdx(n);
    if (n !== null) document.getElementById(`lesson-section-${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  useEffect(() => {
    if (!user || !id) return;
    const l = getLesson(id);
    if (l && lessonUnlocked(l.id, purchases || [])) touchLesson(l.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, purchases]);

  if (!lesson) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="font-serif text-2xl">Lesson not found.</p>
          <Link to="/courses" className="mt-4 inline-block text-primary">Back to courses</Link>
        </div>
      </div>
    );
  }

  const ctx = getLessonContext(lesson.id);
  const course = ctx ? ctx.course : null;
  const instrument = ctx ? ctx.instrument : null;
  const lessonNumber = ctx ? ctx.number : lesson.id;
  const isDone = new Set(progress?.completed_lessons || []).has(lesson.id);
  const nextLesson = nextLessonInCourse(lesson.id);
  const unlocked = lessonUnlocked(lesson.id, purchases || []);
  const playEntry = lessonTrack(lesson.id);

  if (!unlocked && user === null) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <p className="py-32 text-center text-sm text-muted-foreground" data-testid="lesson-loading">Loading lesson…</p>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="lesson-locked">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Lock size={28} />
          </span>
          <h1 className="mt-6 font-serif text-3xl">This lesson is locked</h1>
          <p className="mt-3 text-muted-foreground">
            Lesson {lessonNumber} of {instrument?.label} — {course?.title} is part of the full course.
            Every lesson is part of a paid course. {course?.mini ? "Unlock this Gospel course for £300, or any two Gospel courses for £500." : "Unlock the full path from £500."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to={course?.mini ? "/payment#gospel" : `/payment?instrument=${instrument?.id}&course=${course?.id}#courses`} data-testid="lesson-enrol-button">
              <Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">Pay now</Button>
            </Link>
            <Link to={`/courses/${instrument?.id}/${course?.id}`}>
              <Button variant="outline" className="rounded-full border-border">Back to course</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleQuizComplete = async (score, total, passed) => {
    if (user) await saveQuiz(`lesson-${lesson.id}`, score, total);
    if (!passed) return;
    await completeLesson(lesson.id);
    if (user) toast.success(`Lesson ${lessonNumber} complete! You scored ${score}/${total}.`);
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <article className="mx-auto max-w-3xl px-4 sm:px-8 py-10 lg:py-14">
        <LessonPrevNext lesson={lesson} ctx={ctx} />
        {ctx ? (
          <Breadcrumbs items={[{ label: "All courses", to: "/courses" }, { label: instrument.label, to: `/instruments/${instrument.id}` },
            { label: course.mini ? course.title : `${course.from} → ${course.to}`, to: `/courses/${instrument.id}/${course.id}` }, { label: `Lesson ${lessonNumber}` }]} />
        ) : (
          <Link to="/courses" data-testid="back-to-lessons" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={15} /> All courses
          </Link>
        )}

        <div key={`banner-${lesson.id}`} className="mt-6 animate-in fade-in slide-in-from-top-2 rounded-2xl border-l-4 border-primary bg-primary/10 px-5 py-4 duration-500" data-testid="lesson-number-banner">
          <p className="font-serif text-4xl tracking-tight text-primary sm:text-5xl" data-testid="lesson-number-title">Lesson {lessonNumber}</p>
          <p className="mt-1 text-base font-medium text-foreground" data-testid="lesson-number-subtitle">{lesson.title}</p>
          {ctx && <CourseProgress lessons={ctx.course.lessons} number={lessonNumber} done={new Set(progress?.completed_lessons || [])} />}
        </div>
        <div className="mt-6" data-testid="lesson-logo"><Logo height={52} /></div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary font-serif text-lg text-foreground">
            {lessonNumber}
          </span>
          {instrument && (
            <span className="rounded-full bg-secondary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">
              {instrument.label}
            </span>
          )}
          {course && (
            <span className="rounded-full bg-secondary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">
              {course.tagline}
            </span>
          )}
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
            lesson.level === "Beginner"
              ? "bg-primary/10 text-primary"
              : lesson.level === "Advanced"
              ? "bg-secondary text-primary"
              : "bg-muted text-muted-foreground"
          }`}>
            {lesson.level}
          </span>
          {isDone && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={13} /> Completed
            </span>
          )}
        </div>

        <h1 className="mt-4 font-serif text-4xl tracking-tight sm:text-5xl">{lesson.title}</h1>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{lesson.summary}</p>
        <p className="mt-3 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground" data-testid="lesson-duration">
          <Clock size={14} className="text-primary" /> <strong className="text-foreground">{lesson.minutes} minutes</strong>
          {lesson.plan && <span>· recap {lesson.plan.recap}{lesson.plan.warm ? ` · warm-up ${lesson.plan.warm}` : ""} · lesson {lesson.plan.core}{lesson.plan.play ? ` · play-along ${lesson.plan.play}` : ""} min</span>}
        </p>
        <CourseCertificateCta ctx={ctx} lessonId={lesson.id} />
        {!showQuiz && <WholeLessonListen key={`listen-${lesson.id}`} sections={lesson.sections} rate={rate} onActive={followSection} />}
        <LessonIntro lessonId={lesson.id} />
        <p className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-secondary px-5 py-3.5 text-base text-foreground" data-testid="theme-tip"><SunMoon size={22} className="shrink-0 text-primary" /> Tip: press the light/dark mode button (sun/moon) at the top if it's easier to read in the room you're in — or open Accessibility (bottom left) for bigger text, captions and read-aloud.</p>
        {instrument && (
          <div className="mt-6 overflow-hidden rounded-3xl border border-border">
            {((src) => (
              <div className="relative h-56 sm:h-72">
                <img src={src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-90" />
                <img src={src} alt={`${instrument.label} musician`} className="relative mx-auto h-full w-auto object-contain" data-testid="lesson-hero-image" />
              </div>
            ))(lessonImage(instrument.id, lessonNumber, course?.id))}
          </div>
        )}

        {!showQuiz ? (
          <>
            {instrument && <LessonRecap lesson={lesson} instrumentId={instrument.id} />}
            {lesson.sections.map((s, i) => (
              <motion.section
                key={`${i}-${s.heading}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i, 5) * 0.1 }}
                id={`lesson-section-${i}`}
                data-testid={`lesson-section-${i}`}
                data-reading={readingIdx === i ? "true" : undefined}
                className={`mt-10 scroll-mt-36 rounded-2xl transition-[background-color,box-shadow] duration-300 ${readingIdx === i ? "-mx-4 bg-primary/5 px-4 py-3 ring-2 ring-primary/60" : ""}`}
              >
                {s.theory && (
                  <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary" data-testid={`theory-badge-${i}`}>
                    <GraduationCap size={12} /> {s.theory}
                  </span>
                )}
                {s.gospel && (
                  <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary" data-testid={`gospel-badge-${i}`}>
                    <Church size={12} /> Gospel Corner
                  </span>
                )}
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-serif text-2xl">{s.heading}</h2>
                  <ListenButton id={i} text={`${s.heading}. ${s.text || ""}`} label={s.heading} rate={rate} />
                </div>
                {s.aside ? (
                  <div className="gap-6 sm:flex sm:items-start">
                    <div className="min-w-0 flex-1"><SectionText s={s} i={i} /></div>
                    <div className="mt-4 shrink-0 sm:w-72" data-testid={`lesson-aside-${i}`}><NotationDiagram name={s.aside} /></div>
                  </div>
                ) : <SectionText s={s} i={i} />}
                {s.diagrams?.map((d) => <NotationDiagram key={d} name={d} />)}
                {s.chart === "noteValues" && <NoteValuesChart />}
                {s.ladder && <NoteValueLadder stage={s.ladder} testId={`note-value-ladder-${i}`} />}
                {s.video && <SummaryVideo name={s.video} label={s.videoLabel || "Count-along video · about 2 min"} testId={`count-along-video-${i}`} speeds />}
                {s.chart === "sheetMusic" && <SheetMusicExample />}
                {s.notes?.length > 0 && (
                <div className="mt-5 rounded-2xl border border-border bg-card p-4 sm:p-6">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-mono text-xs uppercase tracking-widest text-primary">
                      {clefLabel(s.clef)}
                    </span>
                    {s.sequence && (
                      <button
                        onClick={() => playSequence(s.sequence, 0.55, 0.5, (k) => setActive(k < 0 ? null : [i, seqToNote(s, k)]))}
                        data-testid={`play-sequence-${i}`}
                        className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                      >
                        <Play size={13} /> Play all
                      </button>
                    )}
                  </div>
                  <StaffViewer clef={s.clef} notes={s.notes} width={560} showLabels showLetters={s.letters !== false} tabFrets={s.tabFrets} tabLines={s.tabLines} time={s.time} playOnClick={s.playOnClick !== false} activeIndex={active && active[0] === i ? active[1] : -1} testId={`lesson-staff-${i}`} />
                  {s.staffCaption ? (
                    <p className="mt-1 text-center text-xs font-bold text-foreground/80" data-testid={`lesson-staff-caption-${i}`}>{s.staffCaption}</p>
                  ) : s.playOnClick !== false && !s.noHint && s.clef !== "none" && (
                    <p className="mt-1 flex items-center justify-center gap-1.5 text-center text-xs font-bold text-foreground/80">
                      <Volume2 size={12} /> Click any note to hear its pitch
                    </p>
                  )}
                  {s.tab && (
                    <pre className="mt-4 overflow-x-auto rounded-xl bg-secondary p-4 font-mono text-xs leading-relaxed text-primary" data-testid={`lesson-tab-${i}`}>
{s.tab.join("\n")}
                    </pre>
                  )}
                </div>
                )}
                {s.tab && !(s.notes?.length > 0) && (
                  <pre className="mt-5 overflow-x-auto rounded-xl bg-secondary p-4 font-mono text-xs leading-relaxed text-primary sm:text-sm" data-testid={`lesson-tab-${i}`}>
{s.tab.join("\n")}
                  </pre>
                )}
                {s.staves && <StaveGrid staves={s.staves} i={i} />}
                {s.diagramsAfter?.map((d) => <NotationDiagram key={d} name={d} />)}
                {s.manuscript && <ManuscriptLinks testId={`lesson-manuscript-${i}`} tabPaper={s.tabPaper} clefPapers={s.clefPapers} drumPaper={s.drumPaper || instrument?.id === "drummers"} worksheet={s.manuscript === "worksheet" ? 1 : Number(String(s.manuscript).replace("worksheet", "")) || 0} />}
                {s.voiceFinder && <VoiceRangeFinder testId={`lesson-voice-range-${i}`} />}
                {s.scaleFinder && <ScaleRangeFinder testId={`lesson-scale-range-${i}`} />}
                {s.choir && <div className="mt-5"><ChoirPractice testId={`lesson-choir-${i}`} /></div>}
                {s.game && (
                  <GameBoundary>
                    {s.game === "rhythmClap" && <RhythmClap testId={`lesson-rhythm-clap-${i}`} levels={s.gameLevels} startLevel={s.gameStart} />}
                    {s.game === "clap68" && <RhythmClap meter="6/8" testId={`lesson-clap68-${i}`} />}
                    {s.game === "barBuilder" && <BarBuilder level={s.gameLevel} testId={`lesson-bar-builder-${i}`} />}
                    {s.game === "restSpotter" && <RestSpotter testId={`lesson-rest-spotter-${i}`} />}
                    {s.game === "flashCards" && <RestFlashCards testId={`lesson-flash-cards-${i}`} />}
                    {s.game === "quaverQuiz" && <QuaverStemQuiz testId={`lesson-quaver-quiz-${i}`} />}
                    {s.game === "beamQuiz" && <><BeamDirectionDiagram /><BeamedQuaverQuiz testId={`lesson-beam-quiz-${i}`} /><BeamBuilder testId={`lesson-beam-builder-${i}`} /></>}
                    {s.game === "quaverQuizBass" && <QuaverStemQuiz clef="bass" testId={`lesson-quaver-quiz-bass-${i}`} />}
                    {s.game === "beamQuizBass" && <><BeamDirectionDiagram clef="bass" /><BeamedQuaverQuiz clef="bass" testId={`lesson-beam-quiz-bass-${i}`} /><BeamBuilder clef="bass" testId={`lesson-beam-builder-bass-${i}`} /></>}
                    {s.game === "toneQuiz" && <><ToneSemitoneQuiz testId={`lesson-tone-quiz-${i}`} /><ToneTapper testId={`lesson-tone-tapper-${i}`} /><AccidentalSpotter testId={`lesson-accidental-spotter-${i}`} /></>}
                    {s.game === "fixRhythm" && <FixRhythm testId={`lesson-fix-rhythm-${i}`} />}
                    {s.game === "timeSigMatch" && <TimeSigMatch level={s.gameLevel} testId={`lesson-time-sig-${i}`} />}
                  </GameBoundary>
                )}
                {s.range && (
                  <div className="mt-5 rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid={`lesson-range-${i}`}>
                    <RangeKeyboard {...s.range} testId={`range-keyboard-${i}`} />
                    <p className="mt-2 text-center text-xs font-bold text-foreground/80">{s.range.caption}</p>
                  </div>
                )}
                {s.terms && <TermsGrid s={s} i={i} />}
                {s.clefs && (
                  <div className={`mt-5 grid gap-3 ${s.clefs.length > 3 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} data-testid={`lesson-clefs-${i}`}>
                    {s.clefs.map((cf) => (
                      <div key={cf.clef} className="rounded-2xl border border-border bg-card p-3">
                        <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{cf.label}</p>
                        <StaffViewer clef={cf.clef} notes={[]} width={200} testId={`clef-${cf.clef}`} />
                      </div>
                    ))}
                  </div>
                )}
                {s.keyboard && (
                  <div className={`mt-5 rounded-2xl border border-border bg-card p-4 sm:p-6 ${s.noteGame && gameOn ? "invisible" : ""}`} aria-hidden={s.noteGame && gameOn} data-testid={`lesson-keyboard-${i}`}>
                    <PianoKeyboard {...s.keyboard} testId={`keyboard-${i}`} />
                    <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs font-bold text-foreground/80"><Volume2 size={12} /> {s.keyboard.caption || "Click any key to hear it"}</p>
                  </div>
                )}
                <GameBoundary>{s.noteGame && <FindTheNote onPlayingChange={setGameOn} />}</GameBoundary>
                {s.chordSheet && (
                  <a href="/media/jt-hub-open-chord-sheet.pdf" download target="_blank" rel="noreferrer" data-testid={`lesson-chord-sheet-${i}`}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-105">
                    Download the free open chord sheet (PDF)
                  </a>
                )}
                {s.guitarPic && <GuitarDiagram testId={`lesson-guitar-${i}`} />}
                <GameBoundary>{s.clefDraw && <ClefDrawing clef={s.clefDraw} testId={`lesson-clef-draw-${i}`} />}</GameBoundary>
                <GameBoundary>{s.grooveBuilder && <GrooveBuilder level={s.grooveBuilder} testId={`lesson-groove-builder-${i}`} />}</GameBoundary>
                {s.partSongs && <VoicePartSongs testId={`lesson-part-songs-${i}`} />}
                {s.kitPic && <DrumKitDiagram testId={`lesson-drumkit-${i}`} />}
                {s.strumPatterns && <StrumPatterns patterns={s.strumPatterns} testId={`lesson-strum-${i}`} />}
                {s.fretboard && (
                  <div className="mt-5 rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid={`lesson-fretboard-${i}`}>
                    <Fretboard labels={s.fretboard.labels} testId={`lesson-fretboard-svg-${i}`} />
                    <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs font-bold text-foreground/80"><Volume2 size={12} /> {s.fretboard.caption}</p>
                  </div>
                )}
                {s.chordBoxes && (
                  <div className="mt-5 flex flex-wrap justify-center gap-4 rounded-2xl border border-border bg-card p-4" data-testid={`lesson-chordboxes-${i}`}>
                    {s.chordBoxes.map((cb, k) => (
                      <button key={cb.name} type="button" onClick={() => strum(cb.frets)} className="rounded-xl p-1 transition-colors hover:bg-primary/10" aria-label={`Strum ${cb.name}`} data-testid={`lesson-chordbox-${i}-${k}`}>
                        <ChordBox name={cb.name} frets={cb.frets} base={cb.base} />
                      </button>
                    ))}
                    <p className="w-full text-center text-xs font-bold text-foreground/80">Chord boxes: strings run low E (left) to high e (right). Tap a box to hear it strummed.</p>
                  </div>
                )}
                {s.chordKeys && <ChordKeys chords={s.chordKeys} testId={`lesson-chord-keys-${i}`} />}
                <GameBoundary>{s.noteGames && <NoteGames instrument={s.noteGames} where={course?.mini ? course.title : undefined} />}</GameBoundary>
                {s.drumBeat && <DrumBeatPlayer />}
                {s.missing && <MissingNotes items={s.missing} testId={`missing-${i}`} title={s.quizTitle || (s.theory ? "Check yourself" : undefined)}
                  onAllCorrect={s.theory && user && course ? () => api.post("/theory/check", { course_key: `${instrument.id}:${course.id}`, lesson_id: lesson.id }).then(() => toast.success("Theory check passed — progress saved")).catch(() => {}) : undefined} />}
                {s.steps && (
                  <div className="mt-5 rounded-2xl border border-primary/25 bg-primary/5 p-5" data-testid={`practice-routine-${i}`}>
                    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><ListChecks size={14} /> Practice routine</p>
                    <ol className="mt-3 space-y-2">
                      {s.steps.map((st, k) => (
                        <li key={k} className="flex gap-3 text-sm text-muted-foreground"><span className="font-mono text-primary">{k + 1}.</span>{st}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </motion.section>
            ))}

            <LessonScores lessonId={lesson.id} user={user} />
            <GameBoundary><MarkGames lessonId={lesson.id} /></GameBoundary>

            {lesson.tips?.length > 0 && (
              <section className="mt-10 rounded-2xl border border-border bg-secondary p-6">
                <h3 className="flex items-center gap-2 font-serif text-xl">
                  <Lightbulb size={18} className="text-primary" /> Memory tips
                </h3>
                <ul className="mt-3 space-y-2">
                  {lesson.tips.map((t, i) => (
                    <li key={t} className="flex gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">♪</span> {t}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {playEntry && instrument && <PlayAlong key={`play-${lesson.id}`} entry={{ ...playEntry, minutes: lesson.plan?.play }} instrumentId={instrument.id}
              lessonId={lesson.id} courseId={course?.id} lessonLabel={`Lesson ${lessonNumber}: ${lesson.title}`} />}

            <Button
              onClick={() => setShowQuiz(true)}
              data-testid="start-quiz-button"
              className="mt-10 w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90"
            >
              Take the knowledge check ({lesson.quiz.length} questions) <ArrowRight size={18} className="ml-2" />
            </Button>
          </>
        ) : (
          <div className="mt-10">
            <QuizEngine
              questions={lesson.quiz}
              quizId={`lesson-${lesson.id}`}
              title={`Lesson ${lessonNumber} knowledge check`}
              passMark={Math.ceil((lesson.quiz.length * 2) / 3)}
              onComplete={handleQuizComplete}
            />
            <CourseCertificateCta ctx={ctx} lessonId={lesson.id} testId="quiz-certificate-cta" />
            <div className="mt-6 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setShowQuiz(false)} className="rounded-full border-border">
                Review lesson
              </Button>
              {nextLesson && (
                <Button
                  onClick={() => nav(`/lessons/${nextLesson.id}`)}
                  data-testid="next-lesson-button"
                  className="rounded-full bg-secondary text-foreground hover:bg-muted"
                >
                  Next: {nextLesson.title} <ArrowRight size={16} className="ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}
        <LessonPrevNext lesson={lesson} ctx={ctx} prefix="lesson-bottom" className="mt-12 border-t border-border pt-6" />
      </article>
      {ctx && <LessonHelper key={`helper-${lesson.id}`} lesson={lesson} courseKey={`${ctx.instrument.id}:${ctx.course.id}`} />}
    </div>
  );
}
